import {
  ColorGroup,
  ThemeColors,
  CategoryData,
  ItemData,
  SidebarCatData,
  ModifierGroupData,
} from "./types";

/* ─── Color picker ─── */

export const DEFAULT_EMPTY_COLOR = "#000000";

export const COLOR_GROUPS: ColorGroup[] = [
  {
    title: "Brand Colors",
    accent: "#6366f1",
    fields: [
      {
        key: "primaryColor",
        label: "Primary Color",
        description: "Modifier group chips, buttons, active states, Add to Cart",
      },
      {
        key: "secondaryColor",
        label: "Secondary Color",
        description: "Item prices, Clear Cart button, Best Seller badge",
      },
      {
        key: "tertiaryColor",
        label: "Tertiary Color",
        description: "Additional accent for highlights and decorative elements",
      },
    ],
  },
  {
    title: "Text Colors",
    accent: "#10b981",
    fields: [
      {
        key: "primaryTextColor",
        label: "Primary Text",
        description: "Item names, category titles, headings",
      },
      {
        key: "secondaryTextColor",
        label: "Secondary Text",
        description: "Descriptions, subtitles, hints, modifier required text",
      },
      {
        key: "tertiaryTextColor",
        label: "Tertiary Text",
        description: "Tertiary text elements and less prominent labels",
      },
      {
        key: "labelColor",
        label: "Label Color",
        description: "Tags, status labels, and badge text",
      },
    ],
  },
  {
    title: "Background",
    accent: "#f59e0b",
    fields: [
      {
        key: "backgroundColor",
        label: "Background Color",
        description: "Main screen background and modifier group section fill",
      },
    ],
  },
];

// The kiosk app's own built-in fallback colors — used whenever a merchant
// hasn't set an override. Sourced from the kiosk app team directly (not
// configurable here), so the Live Preview matches what actually ships.
// Text-color values there are given as Android/Flutter ARGB (#AARRGGBB,
// alpha first); converted below to CSS's #RRGGBBAA (alpha last) — an
// opaque FF alpha collapses to a plain 6-digit hex.
export const DEFAULT_THEME_COLORS: Record<keyof ThemeColors, string> = {
  primaryColor: "#C51E3A",
  secondaryColor: "#FFB800",
  tertiaryColor: "#C51E3A",
  labelColor: "#304FFE",
  primaryTextColor: "#000000", // ARGB #FF000000, alpha FF (opaque)
  secondaryTextColor: "#414141CC", // ARGB #CC414141, alpha CC (~80%)
  tertiaryTextColor: "#FFFFFF", // ARGB #FFFFFFFF, alpha FF (opaque)
  backgroundColor: "#FAFAF9",
};

// Flat list derived from groups — used for API field mapping
export const THEME_FIELDS = COLOR_GROUPS.flatMap((g) => g.fields);

export const initialTheme = (): ThemeColors =>
  Object.fromEntries(THEME_FIELDS.map((f) => [f.key, null])) as ThemeColors;

/* ─── Utilities ─── */

// Resolves a nullable theme color with a fallback default
export const resolveColor = (val: string | null, fallback: string): string =>
  val ?? fallback;

export const getUser = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

/* ─── Preview mock data ─── */

export const MOCK_CATEGORIES: CategoryData[] = [
  { id: 1, label: "Cups of Pasta", emoji: "🍝", selected: true },
  { id: 2, label: "Elios", emoji: "🥘", selected: false },
  { id: 3, label: "Drinks", emoji: "🥤", selected: false },
];

export const MOCK_ITEMS: ItemData[] = [
  { id: 1, name: "Cups of Pasta", desc: "Fast checkout – order multiple cups", price: "$0.00" },
  { id: 2, name: "Create your own CUP", desc: "Best seller", price: "$10.00", badge: true },
  { id: 3, name: "Cheesy Bread", desc: "Small ciabatta with vodka sauce", price: "$2.50" },
  { id: 4, name: "Crispy Chicken Alfredo", desc: "Best seller", price: "$15.71" },
  { id: 5, name: "Cheesy Beef Gnocchi", desc: "Gnocchi in meat sauce", price: "$14.01" },
  { id: 6, name: "Fettuccine Shrimp", desc: "Fettuccine in alfredo sauce", price: "$14.01" },
];

export const MOCK_SIDEBAR_CATS: SidebarCatData[] = [
  { label: "Cups of Pasta", emoji: "🍝", selected: true },
  { label: "Elios", emoji: "🥘", selected: false },
  { label: "Drinks", emoji: "🥤", selected: false },
];

export const MOCK_CART_ITEM = {
  name: "Mamma Mia!",
  emoji: "🍝",
  qty: 1,
  price: "$14.01",
  modifiers: [
    { group: "Protein", item: "1 x Ground beef", price: "Price: $5.41" },
    { group: "Mix-in", item: "1 x Extra sauce", price: "Price: $1.90" },
  ],
};

export const MOCK_CART_TOTALS = {
  subtotal: "$21.32",
  tax: "$1.89",
  taxRate: "8.875%",
  total: "$23.21",
};

export const MOCK_MODIFIER_GROUPS: ModifierGroupData[] = [
  {
    id: 1,
    label: "PASTA",
    required: "Choose one",
    options: ["Fettuccine", "Gnocchi", "Penne"],
    emojis: ["🍝", "🫗", "🍝"],
  },
  {
    id: 2,
    label: "SAUCE",
    required: "Choose one",
    options: ["Alfredo", "Bolognese", "Marinara"],
    emojis: ["🥛", "🍖", "🍅"],
    extraPrice: "+$3.90",
    extraPriceIdx: 1,
  },
  {
    id: 3,
    label: "PROTEIN",
    required: "Optional (up to 2)",
    options: ["Chicken", "Shrimp", "Beef"],
    emojis: ["🍗", "🦐", "🥩"],
  },
];
